import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import axios, { CancelTokenSource } from 'axios'
import { setLoading } from 'redux/store/common/slice'
import { useDebounce } from 'hooks/hooks'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import { DEFAULT_PAGE } from 'components/Table/constants'
import {
  IContractHealthSystem,
  IContractHealthSystemWithHospitals
} from 'features/ContractDetails/Facilities/types'
import { RequestArgs } from 'constants/types'
import { TQueryParams } from 'components/Table/types'
import { Props } from './types'

export const useHSTable = ({ getContractHealthSystems }: Props) => {
  const [tableData, setTableData] = useState<
    IContractHealthSystemWithHospitals[]
  >([])
  const [searchValue, setSearchValue] = useState('')
  const [sortParams, setSortParams] =
    useState<RequestArgs<IContractHealthSystem>>()
  const [firstLoad, setFirstLoad] = useState(true)
  const [totalNumber, setTotalNumber] = useState(0)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()
  const contractDetailsContext = useContractDetailsContext()
  const { details } = contractDetailsContext.state
  const debouncedSearch = useDebounce(searchValue, 500)
  const dispatch = useDispatch()

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  const getTableData = useCallback(
    ({ sortField, sortOrder, page }: RequestArgs<IContractHealthSystem>) => {
      if (!details.uuid) {
        return
      }
      dispatch(setLoading(true))
      if (tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
      const params: TQueryParams = {}
      if (searchValue) {
        params.search = searchValue
      }
      if (sortField) {
        if (sortOrder) {
          params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
        }
        setSortParams({
          sortField,
          sortOrder
        })
      }
      const dataPage = page ? page : pageInfo
      params.limit = dataPage.pageSize
      params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
      tableDataTokenRef.current = axios.CancelToken.source()
      getContractHealthSystems(details.uuid, {
        cancelToken: tableDataTokenRef.current?.token,
        params
      })
        .then((resp) => {
          if (!resp.data?.results) {
            return
          }
          setTableData(
            resp.data.results.map((i) => ({
              ...i,
              key: i.uuid,
              children: i.hospitals?.map((hospital) => ({
                ...hospital,
                hospital_level: true,
                hs_amendment_document: !!i.amendment_document_uuid,
                hs_uuid: i.uuid
              }))
            }))
          )
          setTotalNumber(resp.data.count)
          if (firstLoad) {
            setFirstLoad(false)
          }
        })
        .finally(() => dispatch(setLoading(false)))
    },
    [
      details.uuid,
      dispatch,
      searchValue,
      pageInfo,
      getContractHealthSystems,
      firstLoad
    ]
  )

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => getTableData({ ...sortParams }),
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTableData, debouncedSearch])

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      page
    })
    setPageInfo(page)
  }

  const refreshTableData = useCallback(async () => {
    getTableData({ ...sortParams, ...pageInfo })
  }, [getTableData, pageInfo, sortParams])

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }

  return {
    tableData,
    searchValue,
    sortParams,
    firstLoad,
    totalNumber,
    pageInfo,
    debouncedSearch,
    handleTableChange,
    handleChangePageSize,
    setSearchValue,
    getTableData,
    refreshTableData,
    expandedRowKeys,
    setExpandedRowKeys
  }
}

export type UseHSTableReturnType = ReturnType<typeof useHSTable>
